import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGetV3, axiosPostV3 } from '../../api/axios-handler';

export interface FiActionPayload {
  type: string;
  content_id?: number;
  listener_role_id?: number;
  resource_id?: number;
}

export interface FiChatRequest {
  message: string;
  thread_id?: number | null;
  actions?: FiActionPayload[];
  pathway?: 'stories' | 'listeners' | 'resources' | 'everything';
}

export interface FiToolResult {
  tool: string;
  result: Record<string, unknown> & {
    items?: unknown[];
    query?: string;
    message?: string;
    pathway?: string;
  };
}

export interface FiChatResponse {
  thread_id: number;
  message_history: FiMessageRow[];
  ai_response: string;
  tool_results: FiToolResult[];
}

export interface FiMessageRow {
  role: string;
  content?: string;
  timestamp?: string;
  tool_results?: FiToolResult[];
}

export interface FiConversationDto {
  id: number;
  user_id: number;
  messages: FiMessageRow[];
  actions_taken: Record<string, unknown>[];
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export const fiKeys = {
  all: ['fi'] as const,
  history: () => [...fiKeys.all, 'history'] as const,
};

const FI_CHAT_TIMEOUT_MS = 45_000;

export async function postFiChat(body: FiChatRequest): Promise<FiChatResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FI_CHAT_TIMEOUT_MS);
  try {
    const res = await axiosPostV3('/fi/chat', body, { signal: controller.signal });
    return res.data;
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') {
      const timeoutErr = new Error('Fi chat request timed out');
      timeoutErr.name = 'AbortError';
      throw timeoutErr;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getFiHistory(): Promise<{ conversation: FiConversationDto | null }> {
  const res = await axiosGetV3('/fi/history', {});
  return res.data;
}

export async function postFiReset(threadId: number, actions?: FiActionPayload[]): Promise<void> {
  await axiosPostV3('/fi/reset', { thread_id: threadId, actions: actions ?? [] });
}

export const useFiHistory = (enabled: boolean) => {
  return useQuery({
    queryKey: fiKeys.history(),
    queryFn: getFiHistory,
    enabled,
    staleTime: 30_000,
  });
};

export const useFiChatMutation = () => {
  return useMutation({
    mutationFn: postFiChat,
  });
};

export const useFiResetMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, actions }: { threadId: number; actions?: FiActionPayload[] }) =>
      postFiReset(threadId, actions),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fiKeys.history() });
    },
  });
};
